import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { ClipboardCheck, Plus, Clock, Trash2, Copy, Printer, Eye, Download, CheckCircle, XCircle, MessageSquare, Sparkles, RefreshCw, Send, CalendarClock, Archive } from "lucide-react";
import { motion } from "framer-motion";
import { API } from "@/App";
import { formatDuration, handleBlobDownload } from "./utils";

export function QuizzesTab({ classId, quizzes, isTeacher, headers, onRefresh }) {
  const navigate = useNavigate();

  // Quiz results dialog
  const [quizResultsDialog, setQuizResultsDialog] = useState(false);
  const [selectedQuizResults, setSelectedQuizResults] = useState([]);
  const [selectedQuizTitle, setSelectedQuizTitle] = useState("");
  const [loadingQuizResults, setLoadingQuizResults] = useState(false);

  // Student detail dialog
  const [studentDetailDialog, setStudentDetailDialog] = useState(false);
  const [studentDetailData, setStudentDetailData] = useState(null);
  const [loadingStudentDetail, setLoadingStudentDetail] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [savingFeedback, setSavingFeedback] = useState(false);
  const [selectedQuizId, setSelectedQuizId] = useState("");
  const [regeneratingAiFeedback, setRegeneratingAiFeedback] = useState(false);
  const [approvingAiFeedback, setApprovingAiFeedback] = useState(false);

  // Assign quiz dialog
  const [assignQuizDialog, setAssignQuizDialog] = useState(false);
  const [assignQuizId, setAssignQuizId] = useState(null);
  const [assignSourceQuiz, setAssignSourceQuiz] = useState(null);
  const [teacherClasses, setTeacherClasses] = useState([]);
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  const [assigningQuiz, setAssigningQuiz] = useState(false);

  // Status filter tab (teachers only)
  const [statusTab, setStatusTab] = useState("published");

  // Schedule dialog (from list)
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleQuizId, setScheduleQuizId] = useState(null);
  const [scheduleAt, setScheduleAt] = useState("");
  const [savingSchedule, setSavingSchedule] = useState(false);

  const openSchedule = (quiz) => {
    setScheduleQuizId(quiz.quiz_id);
    const init = quiz.publish_at ? new Date(quiz.publish_at) : new Date(Date.now() + 60 * 60 * 1000);
    const local = new Date(init.getTime() - init.getTimezoneOffset() * 60000);
    setScheduleAt(local.toISOString().slice(0, 16));
    setScheduleDialogOpen(true);
  };

  const submitSchedule = async () => {
    if (!scheduleAt) { toast.error("Pick a date/time"); return; }
    const pa = new Date(scheduleAt);
    if (pa <= new Date()) { toast.error("Publish time must be in the future"); return; }
    setSavingSchedule(true);
    try {
      await axios.post(`${API}/quizzes/${scheduleQuizId}/schedule`, { publish_at: pa.toISOString() }, { headers });
      toast.success("Quiz scheduled");
      setScheduleDialogOpen(false);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to schedule");
    } finally { setSavingSchedule(false); }
  };

  const publishNow = async (quizId) => {
    try {
      await axios.post(`${API}/quizzes/${quizId}/publish`, {}, { headers });
      toast.success("Quiz published");
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to publish");
    }
  };

  const moveToDraft = async (quizId) => {
    if (!window.confirm("Move this quiz to Draft? Students will lose access until republished.")) return;
    try {
      await axios.post(`${API}/quizzes/${quizId}/unpublish`, {}, { headers });
      toast.success("Moved to draft");
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const handleViewQuizResults = async (quiz) => {
    setSelectedQuizTitle(quiz.title);
    setLoadingQuizResults(true);
    setQuizResultsDialog(true);
    try {
      const response = await axios.get(`${API}/quizzes/${quiz.quiz_id}/results`, { headers });
      setSelectedQuizResults(response.data);
    } catch (error) {
      toast.error("Failed to load quiz results");
    } finally {
      setLoadingQuizResults(false);
    }
  };

  const handleDownloadQuizPdf = async (quiz) => {
    try {
      await handleBlobDownload(
        `${API}/quizzes/${quiz.quiz_id}/pdf`,
        `${quiz.title.replace(/[^a-zA-Z0-9 ]/g, '')}_quiz.pdf`,
        headers
      );
      toast.success("Quiz PDF downloaded!");
    } catch (error) {
      toast.error("Failed to download quiz PDF");
    }
  };

  const handleViewStudentDetail = async (quizId, resultId) => {
    setSelectedQuizId(quizId);
    setLoadingStudentDetail(true);
    setStudentDetailDialog(true);
    try {
      const response = await axios.get(`${API}/quizzes/${quizId}/results/${resultId}/detail`, { headers });
      setStudentDetailData(response.data);
      setFeedbackText(response.data.feedback || "");
    } catch (error) {
      toast.error("Failed to load student response");
    } finally {
      setLoadingStudentDetail(false);
    }
  };

  const handleSaveFeedback = async () => {
    if (!studentDetailData) return;
    setSavingFeedback(true);
    try {
      await axios.post(`${API}/quizzes/${selectedQuizId}/results/${studentDetailData.result_id}/feedback`, {
        feedback: feedbackText
      }, { headers });
      toast.success("Feedback saved!");
      setStudentDetailData({ ...studentDetailData, feedback: feedbackText });
    } catch (error) {
      toast.error("Failed to save feedback");
    } finally {
      setSavingFeedback(false);
    }
  };

  const handleRegenerateAiFeedback = async () => {
    if (!studentDetailData) return;
    setRegeneratingAiFeedback(true);
    try {
      const response = await axios.post(
        `${API}/quizzes/${selectedQuizId}/results/${studentDetailData.result_id}/ai-feedback/regenerate`,
        {},
        { headers }
      );
      toast.success("AI feedback regenerated!");
      setStudentDetailData({ ...studentDetailData, ai_feedback: response.data.ai_feedback, ai_feedback_approved: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to regenerate AI feedback");
    } finally {
      setRegeneratingAiFeedback(false);
    }
  };

  const handleApproveAiFeedback = async () => {
    if (!studentDetailData) return;
    setApprovingAiFeedback(true);
    try {
      await axios.post(
        `${API}/quizzes/${selectedQuizId}/results/${studentDetailData.result_id}/ai-feedback/approve`,
        {},
        { headers }
      );
      toast.success("AI feedback approved — now visible to student!");
      setStudentDetailData({ ...studentDetailData, ai_feedback_approved: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve AI feedback");
    } finally {
      setApprovingAiFeedback(false);
    }
  };

  const handleDownloadStudentPdf = (quizId, resultId, studentName) => {
    handleBlobDownload(
      `${API}/quizzes/${quizId}/results/${resultId}/pdf`,
      `${studentName}_response.pdf`,
      headers
    );
  };

  const openAssignQuizDialog = async (quizId) => {
    setAssignQuizId(quizId);
    setSelectedClassIds([]);
    const src = quizzes.find(q => q.quiz_id === quizId);
    setAssignSourceQuiz(src || null);
    try {
      const res = await axios.get(`${API}/classes`, { headers });
      setTeacherClasses(res.data.filter(c => c.class_id !== classId));
    } catch (e) { /* ignore */ }
    setAssignQuizDialog(true);
  };

  const handleAssignQuiz = async () => {
    if (selectedClassIds.length === 0) {
      toast.error("Please select at least one class");
      return;
    }
    setAssigningQuiz(true);
    try {
      const res = await axios.post(`${API}/quizzes/${assignQuizId}/assign`, {
        class_ids: selectedClassIds
      }, { headers });
      toast.success(res.data.message);
      setAssignQuizDialog(false);
      setAssignQuizId(null);
      setSelectedClassIds([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign quiz");
    } finally {
      setAssigningQuiz(false);
    }
  };

  return (
    <>
      <Card className="border-border/50">
        <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <CardTitle className="font-['Manrope']">Quizzes</CardTitle>
            <CardDescription>Class quizzes and assessments</CardDescription>
          </div>
          {isTeacher && (
            <Button
              onClick={() => navigate(`/quiz/create/${classId}`)}
              className="rounded-full bg-primary text-primary-foreground"
              data-testid="create-quiz-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Quiz
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {isTeacher && (
            <Tabs value={statusTab} onValueChange={setStatusTab} className="mb-4">
              <TabsList className="grid grid-cols-3 w-full max-w-md" data-testid="quiz-status-tabs">
                <TabsTrigger value="published" data-testid="tab-published">
                  Published ({quizzes.filter(q => (q.status || "published") === "published").length})
                </TabsTrigger>
                <TabsTrigger value="scheduled" data-testid="tab-scheduled">
                  Scheduled ({quizzes.filter(q => q.status === "scheduled").length})
                </TabsTrigger>
                <TabsTrigger value="draft" data-testid="tab-drafts">
                  Drafts ({quizzes.filter(q => q.status === "draft").length})
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}
          {(() => {
            const visibleQuizzes = isTeacher
              ? quizzes.filter(q => (q.status || "published") === statusTab)
              : quizzes;
            if (visibleQuizzes.length === 0) {
              return (
                <div className="text-center py-12">
                  <ClipboardCheck className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {isTeacher && statusTab === "draft" ? "No drafts yet — save a quiz as draft to work on it later"
                      : isTeacher && statusTab === "scheduled" ? "No scheduled quizzes — schedule one to auto-publish later"
                      : "No quizzes yet"}
                  </p>
                </div>
              );
            }
            return (
            <div className="space-y-3">
              {visibleQuizzes.map((quiz) => (
                <motion.div
                  key={quiz.quiz_id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-4 rounded-xl border border-border/50 hover:border-primary/30 transition-all group"
                  data-testid={`quiz-${quiz.quiz_id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div
                      className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                      onClick={() => !isTeacher && navigate(`/quiz/${quiz.quiz_id}`)}
                    >
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
                        <ClipboardCheck className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-foreground">{quiz.title}</p>
                          {isTeacher && quiz.status === "draft" && (
                            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 text-[10px]">Draft</Badge>
                          )}
                          {isTeacher && quiz.status === "scheduled" && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300 text-[10px]">
                              <CalendarClock className="w-2.5 h-2.5 mr-0.5" />
                              Publishes {quiz.publish_at ? format(new Date(quiz.publish_at), "MMM d, h:mm a") : ""}
                            </Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                          <span className="whitespace-nowrap">{quiz.question_count} questions</span>
                          {quiz.is_timed && (
                            <span className="flex items-center gap-1 text-orange-600 whitespace-nowrap">
                              <Clock className="w-3 h-3" />
                              {quiz.time_limit_minutes} min
                            </span>
                          )}
                          {quiz.due_date && (
                            <span className="whitespace-nowrap">Due {format(new Date(quiz.due_date), "MMM d")}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  {isTeacher ? (
                    <div className="flex flex-wrap items-center gap-2 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                      {quiz.status === "published" && (
                        <Button size="sm" variant="outline" className="rounded-full" onClick={() => handleViewQuizResults(quiz)} data-testid={`view-results-${quiz.quiz_id}`}>
                          Results
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => handleDownloadQuizPdf(quiz)} data-testid={`print-quiz-${quiz.quiz_id}`}>
                        <Printer className="w-3 h-3 mr-1" />
                        Print
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => navigate(`/quiz/edit/${quiz.quiz_id}`)} data-testid={`edit-quiz-${quiz.quiz_id}`}>
                        Edit
                      </Button>
                      {(quiz.status === "published" || quiz.status === "scheduled") && (
                        <Button size="sm" variant="outline" className="rounded-full" onClick={() => openAssignQuizDialog(quiz.quiz_id)} data-testid={`assign-quiz-${quiz.quiz_id}`}>
                          <Copy className="w-3 h-3 mr-1" />
                          Assign
                        </Button>
                      )}
                      {(quiz.status === "draft" || quiz.status === "scheduled") && (
                        <Button size="sm" variant="outline" className="rounded-full" onClick={() => openSchedule(quiz)} data-testid={`reschedule-${quiz.quiz_id}`}>
                          <CalendarClock className="w-3 h-3 mr-1" />
                          {quiz.status === "scheduled" ? "Reschedule" : "Schedule"}
                        </Button>
                      )}
                      {(quiz.status === "draft" || quiz.status === "scheduled") && (
                        <Button size="sm" className="rounded-full bg-primary text-primary-foreground" onClick={() => publishNow(quiz.quiz_id)} data-testid={`publish-now-${quiz.quiz_id}`}>
                          <Send className="w-3 h-3 mr-1" />
                          Publish Now
                        </Button>
                      )}
                      {quiz.status === "published" && (
                        <Button size="sm" variant="outline" className="rounded-full text-amber-700 hover:bg-amber-50" onClick={() => moveToDraft(quiz.quiz_id)} data-testid={`move-to-draft-${quiz.quiz_id}`}>
                          <Archive className="w-3 h-3 mr-1" />
                          Move to Draft
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="destructive"
                        className="rounded-full"
                        onClick={async () => {
                          if (confirm(`Delete "${quiz.title}"? This will also delete all student submissions.`)) {
                            try {
                              await axios.delete(`${API}/quizzes/${quiz.quiz_id}`, { headers });
                              toast.success("Quiz deleted");
                              onRefresh();
                            } catch (error) {
                              toast.error("Failed to delete quiz");
                            }
                          }
                        }}
                        data-testid={`delete-quiz-${quiz.quiz_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <Button size="sm" className="rounded-full bg-primary text-primary-foreground shrink-0" onClick={() => navigate(`/quiz/${quiz.quiz_id}`)}>
                      Take Quiz
                    </Button>
                  )}
                  </div>
                </motion.div>
              ))}
            </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Quiz Results Dialog */}
      <Dialog open={quizResultsDialog} onOpenChange={setQuizResultsDialog}>
        <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Quiz Results: {selectedQuizTitle}</DialogTitle>
            <DialogDescription>Student scores, completion times, and answers</DialogDescription>
          </DialogHeader>
          {loadingQuizResults ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : selectedQuizResults.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No submissions yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <div className="col-span-3">Student</div>
                <div className="col-span-2 text-center">Score</div>
                <div className="col-span-2 text-center">Percentage</div>
                <div className="col-span-2 text-center">Time</div>
                <div className="col-span-3 text-center">Actions</div>
              </div>
              {selectedQuizResults.map((r) => (
                <div key={r.result_id} className="grid grid-cols-12 gap-2 items-center px-3 py-3 rounded-lg bg-muted/30 border border-border/50" data-testid={`quiz-result-${r.result_id}`}>
                  <div className="col-span-3">
                    <p className="font-medium text-sm text-foreground">{r.student_name}</p>
                    <p className="text-xs text-muted-foreground">{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : ""}</p>
                  </div>
                  <div className="col-span-2 text-center">
                    <span className="font-semibold text-sm">{r.score}/{r.total_points}</span>
                  </div>
                  <div className="col-span-2 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                      r.percentage >= 80 ? "bg-green-100 text-green-700" :
                      r.percentage >= 60 ? "bg-yellow-100 text-yellow-700" :
                      "bg-red-100 text-red-700"
                    }`}>
                      {r.percentage}%
                    </span>
                  </div>
                  <div className="col-span-2 text-center text-sm text-muted-foreground">
                    {formatDuration(r.completion_time_seconds)}
                  </div>
                  <div className="col-span-3 flex items-center justify-center gap-1">
                    <Button size="sm" variant="outline" className="rounded-full text-xs h-7 px-2" onClick={() => handleViewStudentDetail(r.quiz_id, r.result_id)} data-testid={`view-answers-${r.result_id}`}>
                      <Eye className="w-3 h-3 mr-1" />
                      Answers
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full text-xs h-7 px-2" onClick={() => handleDownloadStudentPdf(r.quiz_id, r.result_id, r.student_name)} data-testid={`download-response-${r.result_id}`}>
                      <Download className="w-3 h-3 mr-1" />
                      PDF
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Student Quiz Detail / Review Answers Dialog */}
      <Dialog open={studentDetailDialog} onOpenChange={(open) => { setStudentDetailDialog(open); if (!open) setStudentDetailData(null); }}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
          {loadingStudentDetail ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : studentDetailData ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-['Manrope']">{studentDetailData.quiz_title}</DialogTitle>
                <DialogDescription>
                  {studentDetailData.student_name} — Score: {studentDetailData.score}/{studentDetailData.total_points} ({studentDetailData.percentage}%)
                  {studentDetailData.completion_time_seconds && ` — Time: ${formatDuration(studentDetailData.completion_time_seconds)}`}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 mt-2">
                {studentDetailData.questions.map((q, idx) => (
                  <div key={q.question_id} className={`p-4 rounded-xl border ${q.is_correct ? "border-green-200 bg-green-50/50" : "border-red-200 bg-red-50/50"}`} data-testid={`detail-question-${idx}`}>
                    <div className="flex items-start gap-2 mb-2">
                      <span className="font-semibold text-sm text-foreground min-w-[28px]">Q{idx + 1}.</span>
                      <p className="text-sm text-foreground flex-1 whitespace-pre-wrap">{q.question_text}</p>
                      <span className="text-xs text-muted-foreground shrink-0">{q.points} pt{q.points !== 1 ? "s" : ""}</span>
                    </div>
                    {q.options && (
                      <div className="ml-7 space-y-1 mb-2">
                        {q.options.map((opt, oi) => {
                          const letter = String.fromCharCode(65 + oi);
                          const isStudentChoice = q.student_answer === opt || q.student_answer === letter;
                          const isCorrect = q.correct_answer === opt || q.correct_answer === letter;
                          return (
                            <div key={oi} className={`flex items-center gap-2 text-sm px-2 py-1 rounded ${isStudentChoice && isCorrect ? "bg-green-100 text-green-800 font-medium" : isStudentChoice ? "bg-red-100 text-red-800 font-medium" : isCorrect ? "bg-green-50 text-green-700" : "text-muted-foreground"}`}>
                              <span className="w-5">{letter})</span>
                              <span>{opt}</span>
                              {isStudentChoice && !isCorrect && <XCircle className="w-3 h-3 ml-auto text-red-500" />}
                              {isCorrect && <CheckCircle className="w-3 h-3 ml-auto text-green-500" />}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {!q.options && (
                      <div className="ml-7 space-y-1 mb-1">
                        <p className="text-sm"><span className="text-muted-foreground">Student:</span> <span className={q.is_correct ? "text-green-700 font-medium" : "text-red-700 font-medium"}>{q.student_answer || "No answer"}</span></p>
                        {!q.is_correct && <p className="text-sm"><span className="text-muted-foreground">Correct:</span> <span className="text-green-700 font-medium">{q.correct_answer}</span></p>}
                      </div>
                    )}
                    <div className="ml-7 mt-1">
                      {q.is_correct ? (
                        <Badge className="bg-green-100 text-green-700 hover:bg-green-100 text-xs"><CheckCircle className="w-3 h-3 mr-1" />Correct</Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-xs"><XCircle className="w-3 h-3 mr-1" />Incorrect</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* AI Feedback Section */}
              {studentDetailData.ai_feedback && (
                <div className={`mt-4 p-4 rounded-xl border ${studentDetailData.ai_feedback_approved ? "border-green-200 bg-green-50/30" : "border-indigo-200 bg-indigo-50/30"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="flex items-center gap-2 text-sm font-semibold text-indigo-700">
                      <Sparkles className="w-4 h-4" />
                      AI Feedback
                      {studentDetailData.ai_feedback_approved ? (
                        <Badge className="bg-green-100 text-green-700 text-xs ml-1">Approved</Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-700 text-xs ml-1">Pending Approval</Badge>
                      )}
                    </span>
                    <div className="flex items-center gap-1">
                      {!studentDetailData.ai_feedback_approved && (
                        <Button
                          size="sm"
                          className="text-xs h-7 px-3 rounded-full bg-green-600 hover:bg-green-700 text-white"
                          onClick={handleApproveAiFeedback}
                          disabled={approvingAiFeedback}
                          data-testid="approve-ai-feedback-btn"
                        >
                          <CheckCircle className="w-3 h-3 mr-1" />
                          {approvingAiFeedback ? "Approving..." : "Approve"}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-xs h-7 px-2 text-indigo-600 hover:text-indigo-800"
                        onClick={handleRegenerateAiFeedback}
                        disabled={regeneratingAiFeedback}
                        data-testid="regenerate-ai-feedback-btn"
                      >
                        <RefreshCw className={`w-3 h-3 mr-1 ${regeneratingAiFeedback ? "animate-spin" : ""}`} />
                        {regeneratingAiFeedback ? "Regenerating..." : "Regenerate"}
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-indigo-900 whitespace-pre-wrap leading-relaxed" data-testid="ai-feedback-display">{studentDetailData.ai_feedback}</p>
                  {!studentDetailData.ai_feedback_approved && (
                    <p className="text-xs text-amber-600 mt-2">Student cannot see this until you approve it.</p>
                  )}
                </div>
              )}

              {!studentDetailData.ai_feedback && (
                <div className="mt-4 p-4 rounded-xl border border-dashed border-indigo-200 bg-indigo-50/10">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Sparkles className="w-4 h-4" />
                      No AI feedback generated
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs h-7 px-3 text-indigo-600 border-indigo-200"
                      onClick={handleRegenerateAiFeedback}
                      disabled={regeneratingAiFeedback}
                      data-testid="generate-ai-feedback-btn"
                    >
                      <Sparkles className={`w-3 h-3 mr-1 ${regeneratingAiFeedback ? "animate-spin" : ""}`} />
                      {regeneratingAiFeedback ? "Generating..." : "Generate AI Feedback"}
                    </Button>
                  </div>
                </div>
              )}

              {/* Teacher Feedback Section */}
              <div className="mt-6 p-4 rounded-xl border border-border/50 bg-muted/20">
                <Label className="flex items-center gap-2 mb-2 text-sm font-semibold">
                  <MessageSquare className="w-4 h-4" />
                  Teacher Feedback
                </Label>
                <Textarea
                  placeholder="Write feedback for this student..."
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  className="min-h-[80px] mb-3"
                  data-testid="feedback-textarea"
                />
                <div className="flex items-center gap-2">
                  <Button size="sm" className="rounded-full bg-primary text-primary-foreground" onClick={handleSaveFeedback} disabled={savingFeedback} data-testid="save-feedback-btn">
                    {savingFeedback ? "Saving..." : "Save Feedback"}
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => handleDownloadStudentPdf(selectedQuizId, studentDetailData.result_id, studentDetailData.student_name)} data-testid="download-detail-pdf-btn">
                    <Download className="w-3 h-3 mr-1" />
                    Download PDF
                  </Button>
                </div>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Assign Quiz to Classes Dialog */}
      <Dialog open={assignQuizDialog} onOpenChange={(open) => {
        setAssignQuizDialog(open);
        if (!open) { setAssignQuizId(null); setSelectedClassIds([]); setAssignSourceQuiz(null); }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Assign Quiz to Classes</DialogTitle>
            <DialogDescription>
              {assignSourceQuiz?.status === "scheduled"
                ? "Copies will share the same scheduled publish time and auto-publish together."
                : assignSourceQuiz?.status === "draft"
                ? "Copies will be created as Drafts. Publish each one when ready."
                : "Select classes to assign this quiz to."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {teacherClasses.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No other classes available</p>
            ) : (
              <div className="max-h-60 overflow-y-auto border border-border rounded-lg p-2 space-y-1">
                {teacherClasses.map(cls => (
                  <label key={cls.class_id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedClassIds.includes(cls.class_id)}
                      onChange={() => {
                        setSelectedClassIds(prev =>
                          prev.includes(cls.class_id)
                            ? prev.filter(id => id !== cls.class_id)
                            : [...prev, cls.class_id]
                        );
                      }}
                      className="rounded border-border"
                    />
                    <div>
                      <p className="text-sm font-medium">{cls.name}</p>
                      <p className="text-xs text-muted-foreground">{cls.subject} · {cls.students?.length || 0} students</p>
                    </div>
                  </label>
                ))}
              </div>
            )}
            {selectedClassIds.length > 0 && (
              <p className="text-sm text-primary">{selectedClassIds.length} class(es) selected</p>
            )}
            <Button
              onClick={handleAssignQuiz}
              className="w-full rounded-full bg-primary text-primary-foreground"
              disabled={selectedClassIds.length === 0 || assigningQuiz}
              data-testid="confirm-assign-quiz-btn"
            >
              {assigningQuiz ? "Assigning..." : `Assign to ${selectedClassIds.length} Class(es)`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Schedule Quiz Dialog */}
      <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Schedule Quiz Publish</DialogTitle>
            <DialogDescription>The quiz stays hidden from students until this time, then auto-publishes.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="qt-schedule-at">Publish at</Label>
            <Input
              id="qt-schedule-at"
              type="datetime-local"
              value={scheduleAt}
              min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
              onChange={(e) => setScheduleAt(e.target.value)}
              data-testid="qt-schedule-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
            <Button onClick={submitSchedule} disabled={!scheduleAt || savingSchedule} className="bg-primary text-primary-foreground" data-testid="qt-confirm-schedule">
              <Clock className="w-4 h-4 mr-1" />
              {savingSchedule ? "Saving..." : "Schedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
